// Multi-select, align/distribute, grouping and clipboard helpers for the dashboard builder.

export const newWid = () => `w_${Date.now()}_${Math.floor(Math.random() * 100000)}`;
export const newGid = () => `g_${Date.now()}_${Math.floor(Math.random() * 100000)}`;

const L = (w) => ({ x: w.layout?.x || 0, y: w.layout?.y || 0, w: w.layout?.w || 6, h: w.layout?.h || 4 });
const clampX = (x, w, cols) => Math.max(0, Math.min(cols - w, x));

// mode: left | hcenter | right | top | vmiddle | bottom | dist-h | dist-v
export function alignWidgets(widgets, ids, mode, cols = 24) {
  const idSet = new Set(ids);
  const sel = widgets.filter((w) => idSet.has(w.id) && !w.locked);
  if (sel.length < 2) return widgets;
  const patches = {};
  if (["left", "hcenter", "right"].includes(mode)) {
    const minL = Math.min(...sel.map((w) => L(w).x));
    const maxR = Math.max(...sel.map((w) => L(w).x + L(w).w));
    sel.forEach((w) => {
      const l = L(w);
      if (mode === "left") patches[w.id] = { x: minL };
      else if (mode === "right") patches[w.id] = { x: maxR - l.w };
      else patches[w.id] = { x: Math.round((minL + maxR) / 2 - l.w / 2) };
    });
  } else if (["top", "vmiddle", "bottom"].includes(mode)) {
    const minT = Math.min(...sel.map((w) => L(w).y));
    const maxB = Math.max(...sel.map((w) => L(w).y + L(w).h));
    sel.forEach((w) => {
      const l = L(w);
      if (mode === "top") patches[w.id] = { y: minT };
      else if (mode === "bottom") patches[w.id] = { y: maxB - l.h };
      else patches[w.id] = { y: Math.round((minT + maxB) / 2 - l.h / 2) };
    });
  } else if (mode === "dist-h" && sel.length >= 3) {
    const s = [...sel].sort((a, b) => L(a).x - L(b).x);
    const minL = L(s[0]).x;
    const maxR = Math.max(...s.map((w) => L(w).x + L(w).w));
    const sumW = s.reduce((t, w) => t + L(w).w, 0);
    const gap = Math.max(0, maxR - minL - sumW) / (s.length - 1);
    let cur = minL;
    s.forEach((w) => { patches[w.id] = { x: Math.round(cur) }; cur += L(w).w + gap; });
  } else if (mode === "dist-v" && sel.length >= 3) {
    const s = [...sel].sort((a, b) => L(a).y - L(b).y);
    const minT = L(s[0]).y;
    const maxB = Math.max(...s.map((w) => L(w).y + L(w).h));
    const sumH = s.reduce((t, w) => t + L(w).h, 0);
    const gap = Math.max(0, maxB - minT - sumH) / (s.length - 1);
    let cur = minT;
    s.forEach((w) => { patches[w.id] = { y: Math.round(cur) }; cur += L(w).h + gap; });
  }
  return widgets.map((w) => {
    const p = patches[w.id];
    if (!p) return w;
    const l = { ...(w.layout || {}), ...p };
    if (p.x != null) l.x = clampX(p.x, L(w).w, cols);
    if (p.y != null) l.y = Math.max(0, p.y);
    return { ...w, layout: l };
  });
}

// Expand a clicked widget id to its whole group (if grouped)
export function expandToGroup(widgets, id) {
  const w = widgets.find((x) => x.id === id);
  if (!w?.group_id) return [id];
  return widgets.filter((x) => x.group_id === w.group_id).map((x) => x.id);
}

// ---- Cross-dashboard clipboard (localStorage, survives navigation) ----
const CLIP_KEY = "eip_widget_clipboard";

export function copyWidgetsToClipboard(widgets, ids, datasets, sourceDashboardId) {
  const idSet = new Set(ids);
  const sel = widgets.filter((w) => idSet.has(w.id));
  if (!sel.length) return 0;
  const dsMeta = {};
  sel.forEach((w) => {
    if (w.dataset_id && !dsMeta[w.dataset_id]) {
      const d = (datasets || []).find((x) => x.id === w.dataset_id);
      dsMeta[w.dataset_id] = { name: d?.name || "Unknown dataset", schema: (d?.schema || []).map((c) => ({ name: c.name, type: c.type })) };
    }
  });
  try {
    localStorage.setItem(CLIP_KEY, JSON.stringify({
      widgets: JSON.parse(JSON.stringify(sel)), datasets: dsMeta,
      source_dashboard_id: sourceDashboardId, ts: Date.now(),
    }));
  } catch { return 0; }
  return sel.length;
}

export function readWidgetClipboard() {
  try {
    const raw = JSON.parse(localStorage.getItem(CLIP_KEY));
    if (!raw || !Array.isArray(raw.widgets) || !raw.widgets.length) return null;
    return raw;
  } catch { return null; }
}

// ---- Dataset remapping (paste into a dashboard where the source dataset is unavailable) ----

// All source field names a widget references.
export function widgetUsedFields(w) {
  const cfg = w.config || {};
  const out = new Set();
  (cfg.dimensions || []).forEach((d) => d.field && out.add(d.field));
  (cfg.measures || []).forEach((m) => m.field && out.add(m.field));
  (cfg.drilldownPath || []).forEach((d) => d.field && out.add(d.field));
  if (cfg.boxField) out.add(cfg.boxField);
  if (cfg.options?.compare?.field) out.add(cfg.options.compare.field);
  return [...out];
}

// Auto-match source fields to destination schema by name (exact, then case-insensitive, then substring).
export function autoFieldMap(srcFields, destSchema) {
  const destNames = (destSchema || []).map((c) => c.name);
  const lower = Object.fromEntries(destNames.map((n) => [n.toLowerCase(), n]));
  const map = {};
  srcFields.forEach((f) => {
    if (destNames.includes(f)) { map[f] = f; return; }
    if (lower[f.toLowerCase()]) { map[f] = lower[f.toLowerCase()]; return; }
    const sub = destNames.find((n) => n.toLowerCase().includes(f.toLowerCase()) || f.toLowerCase().includes(n.toLowerCase()));
    map[f] = sub || "";
  });
  return map;
}

// Rewrite a widget's config to point at a new dataset using a src->dest field map.
// Governed semantic keys are dropped (semantic models are dataset-specific) -> ad-hoc mode.
export function remapWidgetDataset(w, newDatasetId, fieldMap) {
  const copy = JSON.parse(JSON.stringify(w));
  copy.dataset_id = newDatasetId;
  const cfg = copy.config || {};
  const mapF = (f) => (f && fieldMap[f]) || f;
  (cfg.dimensions || []).forEach((d) => { d.field = mapF(d.field); delete d.key; });
  (cfg.measures || []).forEach((m) => { m.field = mapF(m.field); delete m.key; });
  (cfg.drilldownPath || []).forEach((d) => { d.field = mapF(d.field); });
  if (cfg.boxField) cfg.boxField = mapF(cfg.boxField);
  if (cfg.options?.compare?.field) cfg.options.compare.field = mapF(cfg.options.compare.field);
  if (cfg.options?.condRules) {
    cfg.options.condRules.forEach((r) => {
      Object.entries(fieldMap).forEach(([from, to]) => {
        if (to && typeof r.target === "string" && r.target.includes(from)) r.target = r.target.split(from).join(to);
      });
    });
  }
  if (cfg.measures?.length || cfg.dimensions?.length) cfg.adhoc = true;
  copy.config = cfg;
  return copy;
}

// Prepare clipboard widgets for insertion: fresh ids, fresh group ids, placed below existing content.
export function materializePaste(clipWidgets, existingWidgets, cols) {
  const maxY = existingWidgets.reduce((m, w) => Math.max(m, (w.layout?.y || 0) + (w.layout?.h || 4)), 0);
  const minY = Math.min(...clipWidgets.map((w) => w.layout?.y || 0), 0);
  const gidMap = {};
  const idMap = {};
  const copies = clipWidgets.map((w) => {
    const copy = JSON.parse(JSON.stringify(w));
    copy.id = newWid();
    idMap[w.id] = copy.id;
    copy.locked = false;
    if (copy.group_id) {
      if (!gidMap[copy.group_id]) gidMap[copy.group_id] = newGid();
      copy.group_id = gidMap[copy.group_id];
    }
    const l = copy.layout || {};
    copy.layout = {
      ...l,
      x: clampX(l.x || 0, Math.min(l.w || 6, cols), cols),
      y: (l.y || 0) - minY + maxY,
      w: Math.min(l.w || 6, cols),
      h: l.h || 4,
    };
    return copy;
  });
  // Container children: remap parent ids; orphans (parent not copied) are released to canvas.
  copies.forEach((c) => {
    if (!c.parent_id) return;
    const mapped = idMap[c.parent_id];
    if (mapped) c.parent_id = mapped;
    else {
      const lc = c.layout_child || {};
      c.parent_id = null;
      c.layout = { ...(c.layout || {}), w: Math.min(lc.w || c.layout?.w || 6, cols), h: lc.h || c.layout?.h || 4 };
      c.layout_child = null;
    }
  });
  return copies;
}
